import { fg } from '@atlassian/jira-feature-gating';
import type { childRowsLoaderQuery$variables } from '@atlassian/jira-relay/src/__generated__/childRowsLoaderQuery.graphql';
import { COLUMN_ID_MERGED_COLUMN } from '@atlassian/jira-issue-table-common/src/constants.tsx';

export interface Column {
	id: string;
	mergedColumns?: Column[];
}

interface BaseProps<TColumn extends Column> {
	cloudId: string;
	columns: TColumn[];
	isReparentingEnabled: boolean;
	isTimelineEnabled: boolean;
	actionColumns: string[];
	amountOfColumns: number;
	pageSize: number;
}

interface ChildQueryForJqlProps<TColumn extends Column> extends BaseProps<TColumn> {
	jql: string;
	isHideDoneItemsEnabled: boolean;
}

const getProjectKeyFromIssueKey = (issueKey: string): string => {
	return issueKey.split('-')[0];
};

const getChildQueryVariablesBase = <TColumn extends Column>({
	cloudId,
	columns,
	isReparentingEnabled,
	isTimelineEnabled,
	actionColumns,
	amountOfColumns,
	pageSize,
}: BaseProps<TColumn>) => {
	let fieldSetIds = columns
		.filter((column: TColumn) => !!column.id && !actionColumns.includes(column.id))
		.map((column: TColumn) => column.id)
		.sort();

	const mergedColumnsIds = fg('jira_list_merged_column_gate')
		? (columns.find((col) => col?.id === COLUMN_ID_MERGED_COLUMN)?.mergedColumns ?? []).map(
				(col) => col.id,
			)
		: [];

	fieldSetIds = [...mergedColumnsIds, ...fieldSetIds];

	return {
		cloudId,
		amountOfColumns,
		fieldSetsInput: {
			fieldSetIds,
		},
		fieldSetIds,
		first: pageSize,
		namespace: '',
		shouldQueryFieldSetsById: true,
		filterId: null,
		viewId: null,
		isReparentingEnabled,
		isTimelineEnabled,
	};
};

export const getChildQueryVariablesForJql = <TColumn extends Column>({
	cloudId,
	columns,
	jql,
	isReparentingEnabled,
	isTimelineEnabled,
	actionColumns,
	amountOfColumns,
	pageSize,
	isHideDoneItemsEnabled,
}: ChildQueryForJqlProps<TColumn>) => {
	return {
		...getChildQueryVariablesBase({
			cloudId,
			columns,
			isReparentingEnabled,
			isTimelineEnabled,
			actionColumns,
			amountOfColumns,
			pageSize,
		}),
		issueSearchInput: { jql },
		shouldQueryHasChildren: false,
		...(isHideDoneItemsEnabled &&
			fg('jsc_list_hide_done_items') && {
				viewConfigInput: {
					staticViewInput: {
						isHideDoneEnabled: true,
					},
				},
			}),
	};
};

interface ChildQueryForParentIssueProps<TColumn extends Column, TSortOrder>
	extends BaseProps<TColumn> {
	parentIssueKey: string;
	sortField?: string;
	sortDirection?: TSortOrder;
	jql?: string;
	isReparentingEnabled: boolean;
	isHideDoneItemsEnabled: boolean;
}

export const getChildQueryVariablesForParentIssue = <TColumn extends Column, TSortOrder>({
	cloudId,
	columns,
	parentIssueKey,
	jql,
	isReparentingEnabled,
	isTimelineEnabled,
	isHideDoneItemsEnabled,
	actionColumns,
	amountOfColumns,
	pageSize,
}: ChildQueryForParentIssueProps<TColumn, TSortOrder>): childRowsLoaderQuery$variables => {
	const projectKey = getProjectKeyFromIssueKey(parentIssueKey);

	return {
		...getChildQueryVariablesBase({
			cloudId,
			columns,
			isReparentingEnabled,
			isTimelineEnabled,
			actionColumns,
			amountOfColumns,
			pageSize,
		}),
		issueSearchInput: {
			childIssuesInput: {
				parentIssueKey,
				filterByProjectKeys: [projectKey],
				...(jql
					? {
							jql,
						}
					: {}),
				...(fg('jsc_list_hide_done_items') && { namespace: 'ISSUE_NAVIGATOR' }),
			},
		},
		projectKeys: [projectKey],
		projectKey,
		viewConfigInput: {
			staticViewInput: {
				isHierarchyEnabled: true,
				isGroupingEnabled: false,
				...(fg('jsc_list_hide_done_items') && { isHideDoneEnabled: isHideDoneItemsEnabled }),
			},
		},
		shouldQueryHasChildren: true,
	};
};
